/* eslint-disable camelcase */
import {
  App, Button, Drawer, Flex, Form,
  Input, Select,
  Skeleton,
} from 'antd';
import SourceInput from 'components/SourceInput';
import FormItem from 'components/FormItem';
import FormLayout from 'components/FormLayout';
import LocationSelectInput from 'components/LocationSelectInput';
import UserSelectInput from 'components/UserSelectInput';
import { EMPTY_OBJECT } from 'constants';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { SIC_CATEGORY_OPTIONS } from '../constants';

function DrawerForm({
  data = EMPTY_OBJECT,
  loading,
  open,
  onClose,
  title,
  onSubmit,
  onDelete,
}) {
  return (
    <Drawer
      title={title}
      placement="right"
      onClose={onClose}
      open={open}
      size="large"
      styles={{ body: { padding: 0, paddingBottom: 64 } }}
    >
      {loading ? (
        <FormLayout.Section divider={false}>
          <Skeleton paragraph={{ rows: 10 }} />
        </FormLayout.Section>
      ) : (
        <CompanyForm
          data={data}
          onSubmit={onSubmit}
          onDelete={onDelete}
          onClose={onClose}
          loading={loading}
        />
      )}
    </Drawer>
  );
}

function CompanyForm({
  data, onSubmit, onDelete, onClose,
}) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const country = Form.useWatch('country');

  useEffect(() => {
    if (!data) {
      return;
    }
    form.setFieldsValue(deserialize(data));
    setTimeout(() => {
      form.setFieldValue('stateOfCountry', data.state);
    }, 200);
  }, [data, form]);

  useEffect(() => {
    form.setFieldValue('stateOfCountry', null);
  }, [country?.value, form]);

  const handleSubmit = async (data) => {
    setLoading(true);
    onSubmit(serialize(data))
      .then((res) => {
        message.success(`Company ${id ? 'updated' : 'created'} successfully!`);
        form.resetFields();
      })
      .catch((e) => {

        message.error(getHttpErrorMessage(e));
        captureException(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={deserialize(data)}
      style={{ marginTop: 24 }}
    >
      {/* General info section */}
      <FormLayout.Section title="General info" divider={false}>
        <Flex wrap="wrap" gap={16}>
          <FormItem name="name" label="Company name" rules={[{ required: true, message: 'Please specify the name' }]}>
            <Input />
          </FormItem>
          <FormItem name="website" label="Website">
            <Input addonBefore="https://" />
          </FormItem>
          <FormItem
            name="employee_count"
            label="Employee count"
            rules={[{
              type: 'number',
              transform: (value) => (value === '' || value == null ? undefined : Number(value)),
              message: 'Employee count is not a valid number',
            }]}
          >
            <Input type="number" />
          </FormItem>
          <FormItem name="sic_category" label="SIC category">
            <Select options={SIC_CATEGORY_OPTIONS} />
          </FormItem>
          <FormItem name="owner" label="Owner">
            <UserSelectInput placeholder="Change owner..." />
          </FormItem>
          <FormItem name="source" label="Origin">
            <SourceInput />
          </FormItem>
        </Flex>
      </FormLayout.Section>

      {/* Address info section */}
      <FormLayout.Section title="Address info">
        <Flex gap={16}>
          <FormItem name="market" label="Market" fullWidth>
            <LocationSelectInput
              placeholder="Market"
              mode={undefined}
              labelInValue
              style={{ width: '100%' }}
            />
          </FormItem>
          <FormItem name="zip_code" label="Zip code" fullWidth>
            <Input />
          </FormItem>
        </Flex>
        <FormItem name="address" label="Full address" fullWidth>
          <Input.TextArea rows={4} />
        </FormItem>
      </FormLayout.Section>

      <FormLayout.Footer>
        <Flex justify={isEditMode ? 'space-between' : 'flex-end'} className="w-full">
          <div>
            {onDelete && (
              <Button danger disabled={loading} onClick={onDelete}>
                Delete
              </Button>
            )}
          </div>
          <Flex gap={4}>
            <Button disabled={loading} onClick={onClose}>
              Cancel
            </Button>
            <Button htmlType="submit" type="primary" loading={loading}>
              {isEditMode ? 'Save' : 'Add'}
            </Button>
          </Flex>
        </Flex>
      </FormLayout.Footer>
    </Form>
  );
}

function serialize({
  stateOfCountry,
  country,
  employee_count,
  owner,
  market,
  website,
  ...data
}) {
  return {
    ...data,
    country: country?.label,
    state: stateOfCountry,
    employee_count: employee_count || 0,
    owner_id: owner?.value,
    market_id: market?.value,
    website: website && website.replace('https://', ''),
  };
}

function deserialize({
  state,
  country,
  employee_count,
  owner,
  market,
  ...data
}) {
  return {
    ...data,
    owner: owner && {
      label: owner.email,
      value: owner.id,
    },
    country: country && {
      label: country,
      value: country === 'Canada' ? 'CA' : 'US',
    },
    state: {
      label: state,
      value: state,
    },
    employee_count: employee_count || 0,
    market: market ? { value: market.id, label: market.title } : null,
  };
}

export default DrawerForm;
